import ReneContact from "../components/site/ReneContact";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi, useSeo, eur, fileUrl } from "../lib/api";
import HeroView from "../components/site/HeroView";
import { FindCard, EventCard, SectionHead, Loading, ArtImage } from "../components/site/Bits";

function ProductCard({ a, kind }) {
  const isPrint = kind === "print" || a.print_only;
  const to = isPrint ? `/prints/${a.slug}` : `/kunst/${a.slug}`;
  const price = isPrint ? (a.print_from ? `Print ab ${eur(a.print_from)}` : "") : a.original_price && !a.price_on_request && a.original_status !== "sold" ? `Original ${eur(a.original_price)}` : a.original_status === "sold" ? "Original verkauft" : "Preis auf Anfrage";
  return (
    <div className="group bg-[#FAF8F5] border-2 border-[#121212] flex flex-col" data-testid={`home-card-${kind}-${a.slug}`}>
      <Link to={to} className="block p-3 bg-white border-b-2 border-[#121212]"><ArtImage image={a.image} ratio="aspect-[4/5]" /></Link>
      <div className="p-4 flex flex-col gap-1 flex-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0047FF] font-bold">{isPrint ? "Print" : "Original"}</span>
        <p className="font-display font-black text-xl leading-tight">{a.title}</p>
        <p className="text-sm text-[#6E6B65]">{a.artist?.name}</p>
        <div className="mt-auto pt-3 flex items-center justify-between gap-2"><span className="text-sm font-bold">{price}</span>
          <Link to={to} className="h-10 px-4 inline-flex items-center bg-[#121212] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#0047FF] transition-colors" data-testid={`home-card-view-${a.slug}`}>Ansehen</Link></div>
      </div>
    </div>
  );
}

function Featured({ d }) {
  const [tab, setTab] = useState("all");
  if (!d.featured.length && !d.prints.length) return null;
  const orig = d.featured.filter((a) => !a.print_only).map((a) => ({ a, kind: "original" }));
  const prints = [...new Map([...d.featured.filter((a) => a.print_only), ...d.prints].map((a) => [a.id, a])).values()].map((a) => ({ a, kind: "print" }));
  const list = tab === "original" ? orig : tab === "print" ? prints : [...orig, ...prints].filter((item, i, all) => all.findIndex((other) => other.a.id === item.a.id) === i).slice(0, 8);
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-20" data-testid="home-featured">
      <div className="flex flex-wrap items-end gap-6 mb-10">
        <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mr-auto max-w-2xl" data-testid="home-section-title">{d.hero?.section_title || "Finde dein nächstes Lieblingsstück."}</h2>
        <div className="flex border-2 border-[#121212]" role="tablist">{[["all", "Alle"], ["original", "Originale"], ["print", "Prints"]].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`h-11 px-5 text-sm font-bold uppercase tracking-wider transition-colors ${tab === k ? "bg-[#121212] text-white" : "hover:bg-[#F9D6D2]"}`} data-testid={`home-tab-${k}`}>{l}</button>))}</div>
      </div>
      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">{list.map(({ a, kind }) => <ProductCard key={`${kind}-${a.id}`} a={a} kind={kind} />)}</div>
      <Link to={tab === "print" ? "/prints" : "/kunst"} className="inline-block mt-8 font-bold uppercase tracking-wider text-sm border-b-2 border-[#121212] pb-1" data-testid="home-featured-all">Alle ansehen →</Link>
    </section>
  );
}

function Banner({ d }) {
  const h = d.hero || {};
  if (!h.banner_title) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-24" data-testid="home-banner">
      <div className="grid md:grid-cols-2 bg-[#FFC72C] border-2 border-[#121212]">
        <div className="p-8 sm:p-12 flex flex-col justify-center"><h2 className="font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">{h.banner_title}</h2>{h.banner_sub && <p className="mt-4 max-w-md font-medium">{h.banner_sub}</p>}
          <Link to={h.banner_target || "/fundstuecke"} className="btn-ink mt-6 h-12 px-6 inline-flex items-center w-max font-bold uppercase tracking-wider text-sm" data-testid="home-banner-cta">{h.banner_button || "Entdecken"} →</Link></div>
        {h.banner_image ? <div className="relative min-h-[260px] border-t-2 md:border-t-0 md:border-l-2 border-[#121212] bg-[#121212]"><img src={fileUrl(h.banner_image.url)} alt={h.banner_image.alt || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover" /></div> : <div className="hidden md:block bg-[#F9D6D2] border-l-2 border-[#121212]" />}
      </div>
    </section>
  );
}

function Prints({ d }) {
  if (!d.prints.length) return null;
  return (
    <section className="mt-24 bg-[#EBF0FF] py-20" data-testid="home-prints">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <SectionHead eyebrow="Printangebot" title="Prints für deine Wand" to="/prints" />
        <div className="grid gap-6 grid-cols-2 lg:grid-cols-4">{d.prints.map((a) => (
          <Link key={a.id} to={`/prints/${a.slug}`} className="group bg-white p-3 border border-[#E6DFD5] hover:border-[#0047FF]" data-testid={`home-print-${a.slug}`}>
            <ArtImage image={a.image} ratio="aspect-square" />
            <p className="font-bold mt-3 leading-tight">{a.title}</p><p className="text-sm text-[#6E6B65]">{a.artist.name}</p>
            <p className="text-sm font-semibold text-[#0047FF]">ab {eur(a.print_from)}</p>
          </Link>))}</div>
      </div>
    </section>
  );
}

function Venues({ d }) {
  if (!d.venues.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-24" data-testid="home-venues">
      <SectionHead eyebrow="Kunst vor Ort" title="Hängt gerade in diesen Lokalen" to="/lokale" />
      <div className="grid gap-6 md:grid-cols-3">{d.venues.map((v, i) => (
        <Link key={v.id} to={`/lokale/${v.slug}`} className={`group relative overflow-hidden bg-[#121212] text-white min-h-[260px] p-6 flex flex-col justify-end ${i === 0 ? "md:col-span-2" : ""}`} data-testid={`home-venue-${v.slug}`}>
          {v.images?.[0] && <img src={fileUrl(v.images[0].url)} alt={v.images[0].alt || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 transition-opacity" />}
          <div className="relative"><p className="font-display font-black text-3xl">{v.name}</p><p className="text-sm">{v.city} · {v.current_count} Werk(e) aktuell</p></div>
        </Link>))}</div>
    </section>
  );
}

function Events({ d }) {
  if (!d.events.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-24" data-testid="home-events">
      <SectionHead eyebrow="Kommende Vernissagen" title="Treffen wir uns vor der Wand." to="/vernissagen" />
      <div className="grid gap-4 md:grid-cols-3">{d.events.map((e) => <EventCard key={e.id} e={e} />)}</div>
    </section>
  );
}

function Finds({ d }) {
  if (!d.finds.length && !d.services.length) return null;
  return (
    <section className="mt-24 bg-[#121212] text-[#FAF8F5] py-20 grain" data-testid="home-finds">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 grid lg:grid-cols-[1fr_2fr] gap-12">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FFC72C] mb-2">George</p>
          <h2 className="text-3xl sm:text-4xl font-black">Fundstücke &amp; Einrichtung</h2>
          <p className="mt-4 text-[#cfcac2]">Restaurierte Einzelstücke mit Geschichte – und Räume, in denen Kunst wirken kann.</p>
          <div className="mt-6 flex flex-wrap gap-3"><Link to="/fundstuecke" className="btn-coral h-12 px-5 inline-flex items-center font-semibold" data-testid="home-finds-link">Fundstücke</Link>
            <Link to="/einrichtung" className="h-12 px-5 inline-flex items-center border-2 border-[#FAF8F5] font-semibold hover:bg-[#FAF8F5] hover:text-[#121212] transition-colors" data-testid="home-interior-link">Einrichtung anfragen</Link></div>
        </div>
        <div className="grid grid-cols-2 gap-6 [&_h3]:text-[#FAF8F5] [&_p]:text-[#cfcac2]">{d.finds.slice(0, 2).map((f) => <FindCard key={f.id} f={f} />)}</div>
      </div>
    </section>
  );
}

function Team({ d }) {
  if (!d.team.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-24" data-testid="home-team">
      <SectionHead eyebrow="Wer dahinter steht" title="Betrieb & Kuratierung" to="/ueber-uns" cta="Über uns" />
      <div className="grid md:grid-cols-2 gap-8">{d.team.map((t) => (
        <article key={t.id} className="border-l-4 border-[#FF5A5F] pl-6">
          <h3 className="font-display font-black text-2xl">{t.artist_slug ? <Link to={`/kuenstler/${t.artist_slug}`} className="hover:text-[#0047FF] underline decoration-1 underline-offset-4">{t.name}</Link> : t.name}</h3>
          <p className="text-sm text-[#0047FF] font-semibold">{t.artist_slug ? <Link to={`/kuenstler/${t.artist_slug}`} className="underline underline-offset-4">{t.role_title}</Link> : t.role_title}</p>
          {t.photo && <Link to={t.artist_slug ? `/kuenstler/${t.artist_slug}` : "/ueber-uns"} className="block mt-4 max-w-[240px]" aria-label={`Profil von ${t.name} ansehen`}><img src={fileUrl(t.photo.thumb || t.photo.url)} alt={t.photo.alt || t.name} width={480} height={600} loading="lazy" decoding="async" className="aspect-[4/5] w-full object-cover" /></Link>}
          <p className="mt-4 text-sm text-[#2A2927] line-clamp-3">{t.text}</p>
          {t.artist_slug && <Link to={`/kuenstler/${t.artist_slug}`} className="inline-block mt-4 underline font-semibold text-sm">Profil & Arbeiten ansehen →</Link>}
          {t.name === "René Ghavami" && <ReneContact />}
        </article>))}</div>
    </section>
  );
}

const MAP = { featured: Featured, prints: Prints, venues: Venues, events: Events, finds: Finds, interior: null, team: Team };

export default function Home() {
  const { data, loading } = useApi("/public/home");
  useSeo("Galerie in Lokalen, Prints & Fundstücke");
  if (loading || !data) return <Loading />;
  return (
    <div data-testid="home-page">
      <HeroView hero={data.hero} />
      {data.sections.filter((s) => s.visible && MAP[s.key]).map((s) => { const C = MAP[s.key]; return <C key={s.key} d={data} />; })}
      <Banner d={data} />
    </div>
  );
}

